import { PageFAQGroup } from '@/sanity/pages/PageFAQTemplate';
import { PortableText } from 'next-sanity';
import { customRichTextComponent } from '@/sanity/sanityPortableText';

const glossaryRichText = customRichTextComponent({
  block: {
    normal: ({ children }) => (
      <p className="mb-0 text-[2rem] font-normal leading-[1.5] text-[#1c1f22]">
        {children}
      </p>
    ),
  },
});

const DotGridIcon = () => (
  <svg
    className="w-[2.3rem] h-[2.3rem] shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    width="19"
    height="19"
    fill="none"
    viewBox="0 0 19 19"
  >
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 2.898 15.928)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 2.898 4.344)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 5.79 13.032)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 5.79 7.24)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 5.79 15.928)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 5.79 4.344)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 8.689 13.032)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 8.689 7.24)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 8.689 10.136)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 8.689 15.928)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 8.689 4.344)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 11.58 13.032)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 11.58 7.24)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 11.58 10.136)"
    ></rect>
    <rect
      width="1.448"
      height="1.448"
      fill="#615B50"
      fillOpacity="0.5"
      rx="0.724"
      transform="matrix(1 0 0 -1 14.48 10.136)"
    ></rect>
  </svg>
);

interface FAQGlossaryBlockProps {
  group: PageFAQGroup;
}

export default function FAQGlossaryBlock({ group }: FAQGlossaryBlockProps) {
  if (!group.items?.length) return null;

  return (
    <div className="flex flex-col gap-[5rem]">
      {group.categoryTitle && (
        <h2 className="mb-0 text-[3.5rem] lg:text-[4.5rem] font-semibold tracking-[-0.105rem] lg:tracking-[-0.135rem] leading-[1.01] text-black">
          {group.categoryTitle}
        </h2>
      )}

      <div className="flex flex-col">
        {group.items.map((item, index) => {
          const isLast = index === (group.items?.length ?? 0) - 1;
          return (
            <div key={item._key ?? index} className="relative">
              <div className="flex gap-[2.375rem] items-start p-[4rem]">
                <DotGridIcon />
                <div className="flex flex-col gap-[0.5rem]">
                  {item.question && (
                    <p className="mb-0 text-[2.25rem] font-semibold leading-[1.35] tracking-[-0.0675rem] text-black font-geist">
                      {item.question}
                    </p>
                  )}
                  {item.answer && (
                    <div className="richtext richtext-start prose-p:mb-0 prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-[#1c1f22]">
                      <PortableText
                        value={item.answer}
                        components={glossaryRichText}
                      />
                    </div>
                  )}
                </div>
              </div>

              {!isLast && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(90deg, rgba(0,0,0,0.12) 0px, rgba(0,0,0,0.12) 8px, transparent 8px, transparent 16px)',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
